import React from 'react';
import { X, TrendingUp, TrendingDown, Target, Lightbulb, CheckCircle, Award } from 'lucide-react';
import clsx from 'clsx';
import ReactMarkdown from 'react-markdown';

const FeedbackModal = ({ isOpen, onClose, analysis, scenario }) => {
  if (!isOpen || !analysis) return null;

  const scoreColor = analysis.overallScore >= 80
    ? 'text-green-600 bg-green-50 border-green-200'
    : analysis.overallScore >= 60
    ? 'text-yellow-600 bg-yellow-50 border-yellow-200'
    : 'text-red-600 bg-red-50 border-red-200';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white rounded-xl shadow-sm">
                <Award className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">
                  Анализ тренировки
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  {scenario?.name || 'Сценарий завершен'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Score Badge */}
          <div className="mt-6 flex items-center justify-center">
            <div className={clsx(
              "inline-flex items-center gap-3 px-6 py-3 rounded-2xl border-2",
              scoreColor
            )}>
              <span className="text-4xl font-bold">
                {analysis.overallScore}
              </span>
              <div className="text-left">
                <div className="text-xs font-semibold uppercase tracking-wide">
                  Общая оценка
                </div>
                <div className="text-xs opacity-75">
                  {analysis.metrics.objectivesCompleted} из {analysis.metrics.totalObjectives} целей
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Metrics Grid */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="text-xs text-slate-500 mb-1">Длительность</div>
              <div className="text-2xl font-bold text-slate-900">
                {Math.round(analysis.metrics.duration / 1000)}с
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="text-xs text-slate-500 mb-1">Ваши реплики</div>
              <div className="text-2xl font-bold text-slate-900">
                {analysis.metrics.userTurns}
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="text-xs text-slate-500 mb-1">Реплики AI</div>
              <div className="text-2xl font-bold text-slate-900">
                {analysis.metrics.aiTurns}
              </div>
            </div>
          </div>

          {/* Strengths */}
          {analysis.strengths && analysis.strengths.length > 0 && (
            <div className="bg-green-50 rounded-xl p-5 border border-green-200">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <h3 className="text-sm font-semibold text-green-900 uppercase tracking-wide">
                  Сильные стороны
                </h3>
              </div>
              <ul className="space-y-2">
                {analysis.strengths.map((strength, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-green-800">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{strength}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Improvements */}
          {analysis.improvements && analysis.improvements.length > 0 && (
            <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-200">
              <div className="flex items-center gap-2 mb-3">
                <TrendingDown className="w-5 h-5 text-yellow-600" />
                <h3 className="text-sm font-semibold text-yellow-900 uppercase tracking-wide">
                  Области для улучшения
                </h3>
              </div>
              <ul className="space-y-2">
                {analysis.improvements.map((improvement, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-yellow-800">
                    <Target className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{improvement}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Key Moments */}
          {analysis.keyMoments && analysis.keyMoments.length > 0 && (
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
              <div className="flex items-center gap-2 mb-3">
                <Award className="w-5 h-5 text-blue-600" />
                <h3 className="text-sm font-semibold text-blue-900 uppercase tracking-wide">
                  Ключевые моменты
                </h3>
              </div>
              <ul className="space-y-2">
                {analysis.keyMoments.map((moment, i) => (
                  <li key={i} className="text-sm text-blue-800 pl-2 border-l-2 border-blue-300">
                    {moment}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          {analysis.recommendations && analysis.recommendations.length > 0 && (
            <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="w-5 h-5 text-purple-600" />
                <h3 className="text-sm font-semibold text-purple-900 uppercase tracking-wide">
                  Рекомендации
                </h3>
              </div>
              <ul className="space-y-2">
                {analysis.recommendations.map((recommendation, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-purple-800">
                    <div className="w-5 h-5 rounded-full bg-purple-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-purple-700">{i + 1}</span>
                    </div>
                    <span>{recommendation}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Detailed Feedback */}
          {analysis.detailedFeedback && (
            <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
                Детальная обратная связь
              </h3>
              <div className="text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none">
                <ReactMarkdown
                  components={{
                    table: ({node: _node, ...props}) => (
                      <div className="overflow-x-auto my-4">
                        <table className="min-w-full divide-y divide-slate-200 border border-slate-300" {...props} />
                      </div>
                    ),
                    thead: ({node: _node, ...props}) => (
                      <thead className="bg-slate-50" {...props} />
                    ),
                    th: ({node: _node, ...props}) => (
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-900 uppercase tracking-wider border-b border-slate-200" {...props} />
                    ),
                    td: ({node: _node, ...props}) => (
                      <td className="px-4 py-2 text-sm text-slate-700 border-b border-slate-100" {...props} />
                    ),
                    tr: ({node: _node, ...props}) => (
                      <tr className="hover:bg-slate-50" {...props} />
                    ),
                  }}
                >
                  {analysis.detailedFeedback}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-between gap-4">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
          >
            Закрыть
          </button>
          <button
            onClick={() => window.location.href = '/scenarios'}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
          >
            Выбрать новый сценарий
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
